import {
  AddOutline,
  CloseOutline,
  MaximizeOutline,
  MinimizeOutline,
  MoveOutline,
  RemoveOutline,
} from '@aurora/icons-react';
import { Button, ButtonGroup, Toolbar } from '@aurora/widgets-react';
import React, { useCallback } from 'react';
import Control from 'react-leaflet-control';
import { LayerPicker } from '../../LayerPicker/LayerPicker';
import { TimedGroup } from '../TimedGroup';
import { useTranslation } from 'react-i18next';
import styles from './MapControls.scss';
import { StepControls } from '../StepControls';

export interface MapControlsProps {
  setLayer: (layer: TimedGroup) => void;
  currentLayer: TimedGroup;
  layers: TimedGroup[];
  isFullscreen: boolean;
  maxZoom: number;
  minZoom: number;
  currentZoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  currentTimeIndex: number;
  times: string[];
  minimizeMap: () => void;
  maximizeMap: () => void;
  hasTap: boolean;
  setTap: (val: boolean) => void;
  layout: 'tall' | 'wide' | 'tiny';
  mustToggleTap: boolean;
  setCurrentTimeIndex: (val: number) => void;
  stepControlFormat: string;
}

const VERTICAL_MARGIN = { marginTop: '0.5rem' };
const HORIZONTAL_MARGIN = { marginLeft: '0.5rem' };

export const NarrowMapControls = () => {};

export const MapControls: React.FunctionComponent<MapControlsProps> = ({
  isFullscreen,
  currentZoom,
  maxZoom,
  minZoom,
  zoomIn,
  zoomOut,
  maximizeMap,
  minimizeMap,
  hasTap,
  setTap,
  layout,
  currentTimeIndex,
  times,
  setCurrentTimeIndex,
  mustToggleTap,
  setLayer,
  layers,
  currentLayer,
  stepControlFormat,
}) => {
  const { t } = useTranslation();

  const toggleTap = useCallback(() => setTap(!hasTap), [hasTap, setTap]);

  const marginStyle = layout === 'tall' ? VERTICAL_MARGIN : HORIZONTAL_MARGIN;
  const controls = (
    <Control position="topright">
      <Toolbar floating vertical={layout === 'tall'}>
        <ButtonGroup>
          <Button
            variant={Button.variants.secondary}
            icon={<AddOutline />}
            onClick={zoomIn}
            disabled={currentZoom >= maxZoom}
            label={t('zoomIn')}
            iconOnly={true}
          />
          <Button
            variant={Button.variants.secondary}
            icon={<RemoveOutline />}
            onClick={zoomOut}
            disabled={currentZoom <= minZoom}
            label={t('zoomOut')}
            iconOnly={true}
          />
        </ButtonGroup>
        <ButtonGroup style={marginStyle}>
          <Button
            variant={Button.variants.secondary}
            icon={isFullscreen ? <MinimizeOutline /> : <MaximizeOutline />}
            onClick={isFullscreen ? minimizeMap : maximizeMap}
            label={isFullscreen ? t('exitFullscreen') : t('enterFullscreen')}
            iconOnly={true}
          />
        </ButtonGroup>
        {!mustToggleTap ? (
          <></>
        ) : (
          <ButtonGroup style={marginStyle}>
            <Button
              variant={Button.variants.secondary}
              icon={hasTap ? <CloseOutline /> : <MoveOutline />}
              onClick={toggleTap}
              label={hasTap ? t('deactivatePan') : t('activatePan')}
              iconOnly={true}
            />
          </ButtonGroup>
        )}
      </Toolbar>
    </Control>
  );

  const fullscreenToolbar = (
    <Control position="bottomleft">
      <Toolbar className={styles.timeControls} floating>
        <StepControls
          variant="primary"
          format={stepControlFormat}
          {...{
            setCurrentTimeIndex,
            times,
            currentTimeIndex,
          }}
        />
      </Toolbar>
    </Control>
  );

  const layerPicker = layers.length ? (
    <Control position="topleft">
      <LayerPicker {...{ setLayer, layers, currentLayer }} layout="s" />
    </Control>
  ) : null;
  if (isFullscreen) {
    return (
      <>
        {layerPicker}
        {controls}
        {fullscreenToolbar}
      </>
    );
  } else {
    return (
      <>
        {layerPicker}
        {controls}
      </>
    );
  }
};
